import { SubscriptionTier } from "./types";
import {
	OrganizationEntityType,
	BankAccountEntityType,
	ProfileEntityType,
} from "@/lib/supabase/supabaseTypes";
import { RegionConfig } from "../../types/region.types";
import { PriceList } from "../../types/price-list.types";

export { showNotification } from "@/lib/notification";

/**
 * Returns true if the user has the full dentLAB feature set.
 * - "full" and "trial" tiers have full features (jobs, invoices, etc.)
 * - "eligible" and "expired" tiers only have communication features
 */
export const hasFullFeatureSet = (
	subscriptionTier: SubscriptionTier | null
): boolean => {
	return subscriptionTier === "full" || subscriptionTier === "trial";
};

export { showMessageNotification } from "../../components/notifications/message-notification.component";

export interface SetupStepStatus {
	stepNumber: number;
	stepKey: string;
	label: string;
	isComplete: boolean;
	missingFields: string[];
}

export interface SetupStatus {
	isIncomplete: boolean;
	steps: SetupStepStatus[];
}

export const getSetupStatus = (
	organization: OrganizationEntityType | null,
	profile: ProfileEntityType | null,
	activatedRegions: RegionConfig[],
	bankAccountsLookup: Record<string, BankAccountEntityType>,
	priceLists: PriceList[]
): SetupStatus => {
	const steps: SetupStepStatus[] = [];

	const personalInfoMissing: string[] = [];
	if (!profile?.first_name && !profile?.last_name) {
		personalInfoMissing.push("Vorname oder Nachname");
	}
	steps.push({
		stepNumber: 1,
		stepKey: "personal_info",
		label: "Persönliche Informationen",
		isComplete: personalInfoMissing.length === 0,
		missingFields: personalInfoMissing,
	});

	const orgInfoMissing: string[] = [];
	if (!organization?.name) orgInfoMissing.push("Name");
	if (!organization?.street) orgInfoMissing.push("Strasse");
	if (!organization?.city && !organization?.postal_code) {
		orgInfoMissing.push("Stadt/PLZ");
	}
	steps.push({
		stepNumber: 2,
		stepKey: "organization_info",
		label: "Unternehmensinformationen",
		isComplete: orgInfoMissing.length === 0,
		missingFields: orgInfoMissing,
	});

	const regionMissing: string[] = [];
	if (activatedRegions.length === 0) regionMissing.push("Region aktivieren");
	if (organization?.default_activated_region_id === null)
		regionMissing.push("Standardregion setzen");
	steps.push({
		stepNumber: 3,
		stepKey: "region",
		label: "Region",
		isComplete: regionMissing.length === 0,
		missingFields: regionMissing,
	});

	const bankMissing: string[] = [];
	if (Object.keys(bankAccountsLookup).length === 0)
		bankMissing.push("Bankkonto hinzufügen");
	steps.push({
		stepNumber: 4,
		stepKey: "bank_account",
		label: "Bankkonto",
		isComplete: bankMissing.length === 0,
		missingFields: bankMissing,
	});

	const priceListMissing: string[] = [];
	if (priceLists.length === 0) priceListMissing.push("Preisliste erstellen");
	if (organization?.default_price_list_id === null)
		priceListMissing.push("Standard-Preisliste setzen");
	steps.push({
		stepNumber: 5,
		stepKey: "price_list",
		label: "Preisliste",
		isComplete: priceListMissing.length === 0,
		missingFields: priceListMissing,
	});

	const isIncomplete = steps.some((step) => !step.isComplete);

	return { isIncomplete, steps };
};
